<template>
    <div class="upload_component">
        <input
            type="file"
            ref="fileInput"
            class="upload_file_input"
            accept="audio/mp3,audio/mpeg,audio/wav"
            :aria-label="$t('upload.ariaLabel')"
            @change="handleFileSelect"
        >
        <div class="upload_trigger">
            <slot>
                <span v-if="!isLoading && !message">
                    <i class="bi bi-soundwave context_menu_icon"></i>
                    {{ $t('upload.uploadTrack') }}
                </span>
                <span v-if="isLoading">
                    {{ progress }}%
                </span>
                <span v-if="message">
                    {{ message }}
                </span>
            </slot>
        </div>
    </div>
</template>

<script>
import {getMP3Tags} from '@/scripts/utils.js';
import { hapticMixin } from '@/scripts/haptic.js';
import storageService from '@/scripts/services/StorageService.js';
import {uuidv4} from '@/scripts/uuid.js';

let libpartyPromise = null;
const ANALYSIS_TARGET_SAMPLE_RATE = 16_000;

async function loadLibparty() {
    if (libpartyPromise) {
        return libpartyPromise;
    }

    libpartyPromise = import(/* webpackIgnore: true */ '/libparty/libparty.js')
        .then(async (module) => {
            await module.default();
            return module;
        })
        .catch((error) => {
            libpartyPromise = null;
            throw error;
        });

    return libpartyPromise;
}

export default {
    name: 'UploadLocal',
    mixins: [hapticMixin],
    props: {
        projectId: {
            type: String,
            default: null
        }
    },
    computed: {
        storageType() {
            return storageService.state.storageType
        }
    },
    data() {
        return {
            isLoading: false,
            progress: 0,
            message: '',
            isError: false,
            analyzingFile: false
        }
    },
    methods: {
        triggerFileSelect() {
            const input = this.$refs.fileInput
            if (input && typeof input.click === 'function') {
                input.click()
            } else {
                console.error('fileInput ref not available')
            }
        },
        async handleExternalFiles(fileList) {
            if (!fileList || fileList.length === 0) {
                return
            }

            const files = Array.from(fileList)
            for (const file of files) {
                await this.saveFile(file)
            }
        },
        showMessage(message, isError = false) {
            this.message = message
            this.isError = isError

            setTimeout(() => {
                this.message = ''
                this.isError = false
            }, 10000)
        },
        async analyzeAudio(preparedAudio) {
            const results = {
                bpm: null,
                scale: {
                    key: null,
                    mode: null
                },
                lufs: null
            }

            try {
                const { analyze_stereo, analyze_mono } = await loadLibparty();
                let analysis;
                if (preparedAudio.right) {
                    analysis = analyze_stereo(preparedAudio.left, preparedAudio.right, preparedAudio.sampleRate);
                } else {
                    analysis = analyze_mono(preparedAudio.left, preparedAudio.sampleRate);
                }

                results.bpm = analysis.bpm ?? null;
                results.lufs = Number.isFinite(analysis.integrated_lufs)
                    ? analysis.integrated_lufs
                    : null;
                results.scale.key = analysis.key ?? null;
                results.scale.mode = analysis.mode ?? null;
            } catch (error) {
                console.warn('libparty analysis failed:', error)
            }

            return results
        },
        handleFileSelect(event) {
            const file = event.target.files[0]
            if (file) {
                this.saveFile(file)
            }
            event.target.value = ''
        },
        async saveFile(file) {
            this.isLoading = true
            this.progress = 0
            this.message = ''

            return new Promise((resolve) => {
                try {
                    const reader = new FileReader()

                    reader.onprogress = (event) => {
                        if (event.lengthComputable) {
                            this.progress = Math.round((event.loaded / event.total) * 100)
                        }
                    }

                    reader.onload = async (event) => {
                        try {
                            const arrayBuffer = event.target.result
                            this.analyzingFile = true
                            this.showMessage(this.$t('upload.analyzing'), false)

                            const AudioContext = window.AudioContext || window.webkitAudioContext;
                            const audioContext = new AudioContext();
                            let audioBuffer;
                            try {
                                audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));
                            } finally {
                                audioContext.close();
                            }

                            const preparedAudio = this.prepareAudioForAnalysis(audioBuffer, ANALYSIS_TARGET_SAMPLE_RATE)
                            const analysisPromise = this.analyzeAudio(preparedAudio)
                            const waveformPromise = this.generateWaveformDataUrlFromChannels(preparedAudio.left, preparedAudio.right)
                            const durationPromise = Promise.resolve(audioBuffer.duration)

                            const [analysisResults, waveformDataUrl, duration] = await Promise.all([
                                analysisPromise,
                                waveformPromise,
                                durationPromise
                            ]);

                            if (!waveformDataUrl) {
                                throw new Error('Waveform generation failed.')
                            }
                            //const hash = await this.hash(arrayBuffer.slice(0))
                            const { artistName, tracktitle, tagbpm, tracknum } = await getMP3Tags(arrayBuffer.slice(0));
                            const id = uuidv4();
                            const filepath = await storageService.uploadFile(arrayBuffer.slice(0), file.name)
                            //console.log("File data: ",filepath)

                            let finalWaveformDataUrl = waveformDataUrl;
                            if(this.storageType == 'remote'){
                                finalWaveformDataUrl = this.dataUrlToBlob(waveformDataUrl)
                            }

                            // Upload waveform as separate file and get its ID
                            const waveformId = await storageService.uploadFile(finalWaveformDataUrl, `waveform_${file.name}.png`)
                            //console.log("Waveform data: ", waveformId)
                            const intLufs = analysisResults.lufs == null ? null : Math.round(analysisResults.lufs * 100);
                            const meta = {
                                id: id,
                                file: filepath,
                                size: file.size,
                                filename: file.name,
                                upload_date: new Date().toISOString(),
                                bpm: tagbpm ?? analysisResults.bpm,
                                key: analysisResults.scale.key ?? null,
                                mode: analysisResults.scale.mode ?? null,
                                duration: Math.round(duration * 1000) ?? null,
                                lufs: intLufs,
                                waveform: waveformId,
                                effects: "",
                                project: null,
                                num: tracknum,
                                artist: artistName,
                                name: tracktitle ?? file.name,
                                notes: ""
                            }
                            const created = await storageService.createTrack(meta)
                            if (created && this.projectId) {
                                await storageService.addTrackToProject(id, this.projectId)
                            }
                            this.analyzingFile = false
                            this.showMessage(this.$t('upload.fileAdded', { name: file.name }), false)
                        } catch (error) {
                            this.analyzingFile = false
                            this.showMessage(this.$t('upload.errorWithMessage', { message: error.message }), true)
                            console.error(error)
                        } finally {
                            this.isLoading = false
                            resolve()
                        }
                    }

                    reader.onerror = () => {
                        console.log('Error reading file')
                        this.showMessage(this.$t('upload.fileReadError'), true)
                        this.isLoading = false
                        resolve()
                    }

                    reader.readAsArrayBuffer(file)
                } catch (error) {
                    this.showMessage(this.$t('upload.errorWithMessage', { message: error.message }), true)
                    console.error(error)
                    this.isLoading = false
                    resolve()
                }
            })
        },
        async hash(buffer) {
            const digest = await crypto.subtle.digest('SHA-256', buffer)
            return [...new Uint8Array(digest)]
                .map(x => x.toString(16).padStart(2, '0'))
                .join('')
        },
        prepareAudioForAnalysis(audioBuffer, targetSampleRate) {
            const sampleRateIn = audioBuffer.sampleRate
            const needsResample = sampleRateIn !== targetSampleRate
            const leftSource = audioBuffer.getChannelData(0)
            const rightSource = audioBuffer.numberOfChannels > 1
                ? audioBuffer.getChannelData(1)
                : null

            if (!needsResample) {
                return {
                    left: leftSource,
                    right: rightSource,
                    sampleRate: sampleRateIn
                }
            }

            return {
                left: this.resampleLinear(leftSource, sampleRateIn, targetSampleRate),
                right: rightSource ? this.resampleLinear(rightSource, sampleRateIn, targetSampleRate) : null,
                sampleRate: targetSampleRate
            }
        },
        resampleLinear(input, sampleRateIn, sampleRateOut) {
            if (!(input instanceof Float32Array) || input.length === 0) {
                return new Float32Array(0)
            }
            if (sampleRateIn === sampleRateOut) {
                return input
            }

            const outputLength = Math.max(1, Math.round((input.length * sampleRateOut) / sampleRateIn))
            if (outputLength === 1) {
                return new Float32Array([input[0]])
            }

            const output = new Float32Array(outputLength)
            const factor = (input.length - 1) / (outputLength - 1)
            for (let i = 0; i < outputLength; i += 1) {
                const position = i * factor
                const index = Math.floor(position)
                const fraction = position - index
                const next = Math.min(index + 1, input.length - 1)
                output[i] = input[index] * (1 - fraction) + input[next] * fraction
            }
            return output
        },
        generateWaveformDataUrlFromChannels(left, right, options = {}) {
            const width = options.width || 500
            const height = options.height || 80
            const color = options.color || '#000000'
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')

            if (!ctx || !(left instanceof Float32Array) || left.length === 0) {
                return null
            }

            const rightChannel = right instanceof Float32Array ? right : left
            const segmentLen = Math.max(1, Math.floor(left.length / width))
            const halfHeight = height / 2
            const bars = new Array(width)
            let maxAmp = 0

            for (let i = 0; i < width; i += 1) {
                const start = i * segmentLen
                const len = Math.min(segmentLen, left.length - start)
                if (len <= 0) {
                    bars[i] = [0, 0]
                    continue
                }
                const pos = this.bufferMeasure(left, start, len)
                const neg = this.bufferMeasure(rightChannel, start, len)
                bars[i] = [pos, neg]
                const localMax = Math.max(pos, neg)
                if (localMax > maxAmp) {
                    maxAmp = localMax
                }
            }

            ctx.fillStyle = color
            ctx.fillRect(0, Math.floor(halfHeight - 1), width, 1)

            if (maxAmp <= 0) {
                return canvas.toDataURL('image/png')
            }

            const scale = halfHeight / maxAmp
            for (let i = 0; i < width; i += 1) {
                const [pos, neg] = bars[i]
                const posPx = Math.max(1, Math.ceil(pos * scale))
                const negPx = Math.max(1, Math.ceil(neg * scale))
                const x = i
                const yPos = Math.floor(halfHeight - posPx)
                const yNeg = Math.floor(halfHeight)
                ctx.fillRect(x, yPos, 1, posPx)
                ctx.fillRect(x, yNeg, 1, negPx)
            }

            return canvas.toDataURL('image/png')
        },
        bufferMeasure(data, start, len) {
            let sum = 0
            const end = start + len
            for (let i = start; i < end; i += 1) {
                const v = data[i]
                sum += v * v
            }
            return Math.sqrt(sum / len)
        },
        dataUrlToBlob(dataUrl) {
            const [header, base64] = dataUrl.split(',');
            const mime = header.match(/:(.*?);/)[1];
            const binary = atob(base64);
            const array = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
                array[i] = binary.charCodeAt(i);
            }
            return new Blob([array], { type: mime });
        }
    }
}

</script>
<style scoped>
.upload_component {
    position: relative;
}

.upload_file_input {
    position: absolute;
    inset: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
}

.upload_trigger {
    position: relative;
}
</style>
